import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Observable, of} from 'rxjs';
import {Action, Store} from '@ngrx/store';
import * as fromActions from './team.actions';
import {catchError, map, mergeMap, switchMap, withLatestFrom} from 'rxjs/operators';
import {EmployeeService} from '../../shared/services/employee.service';
import {TeamState} from './team.reducer';
import {selectTeamsLoaded} from './team.selectors';
import {IResponse, ITeam} from '@signal/asp-data-commons';
import {toData} from '../app.adapter';

@Injectable()
export class TeamEffects {

  
  loadTeams: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadTeams>(
      fromActions.TeamActionTypes.LOAD_TEAMS
    ),
    withLatestFrom(this.teamsState.select(selectTeamsLoaded)),
    mergeMap(([action, serviceRequestsLoaded]) => {
        const payload = action instanceof fromActions.LoadTeams ? action.payload : undefined;
        if (!(payload.forceReload || !serviceRequestsLoaded)) {
          return of(new fromActions.LoadTeamsNoOp());
        }
        return this.employeeService.getTeams().pipe(
          switchMap((response: IResponse<ITeam[]>) => toData(response)),
          map((teams: ITeam[]) => new fromActions.LoadTeamsSuccess({teams})),
          catchError(err => of(new fromActions.LoadTeamsFail({error: err})))
        )
      }
    )
  ));

  
  addTeam: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.AddTeam>(
      fromActions.TeamActionTypes.ADD_TEAM
    ),
    mergeMap((action: fromActions.AddTeam) => {
        const payload = action instanceof fromActions.AddTeam ? action.payload : undefined;
        return this.employeeService.createTeam(payload.team).pipe(
          switchMap((response: IResponse<ITeam>) => toData(response, 201)),
          map((team: ITeam) => new fromActions.AddTeamSuccess({team})),
          catchError(err => of(new fromActions.AddTeamFail({error: err})))
        )
      }
    )
  ));

  
  updateTeam: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.UpdateTeam>(
      fromActions.TeamActionTypes.UPDATE_TEAM
    ),
    mergeMap((action: fromActions.UpdateTeam) => {
        const payload = action instanceof fromActions.UpdateTeam ? action.payload : undefined;
        return this.employeeService.updateTeam(payload.teamUpdate).pipe(
          switchMap((response: IResponse<ITeam>) => toData(response)),
          map((team: ITeam) => new fromActions.UpdateTeamSuccess({team})),
          catchError(err => of(new fromActions.UpdateTeamFail({error: err})))
        )
      }
    )
  ));

  
  deleteTeam: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.DeleteTeam>(
      fromActions.TeamActionTypes.DELETE_TEAM
    ),
    mergeMap((action: fromActions.DeleteTeam) => {
        const payload = action instanceof fromActions.DeleteTeam ? action.payload : undefined;
        return this.employeeService.deleteTeam(payload.id).pipe(
          switchMap((response: IResponse<ITeam>) => toData(response)),
          map((team: ITeam) => new fromActions.DeleteTeamSuccess({team})),
          catchError(err => of(new fromActions.DeleteTeamFail({error: err})))
        )
      }
    )
  ));

  
  updateEmployeeDetails: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.UpdateEmployeeDetails>(
      fromActions.TeamActionTypes.UPDATE_EMPLOYEE_DETAILS
    ),
    mergeMap((action: fromActions.UpdateEmployeeDetails) => {
        const payload = action instanceof fromActions.UpdateEmployeeDetails ? action.payload : undefined;
        return this.employeeService.putTeam(payload.teamUpdate).pipe(
          switchMap((response: IResponse<ITeam[]>) => toData(response)),
          map((teams: ITeam[]) => new fromActions.UpdateEmployeeDetailsSuccess({teams})),
          catchError(err => of(new fromActions.UpdateEmployeeDetailsFail({error: err})))
        )
      }
    )
  ));


  constructor(
    private actions$: Actions,
    private employeeService: EmployeeService,
    private teamsState: Store<TeamState>
  ) {
  }
}

